(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Shopify Cart API helpers
  // ---------------------------------------------------------------------------

  // Collect section IDs from every cart-items-component on the page.
  // Shopify will render updated HTML for each section and return it in the
  // add-to-cart response — no extra API call needed.
  function getCartSectionIds() {
    /** @type {string[]} */
    var ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
      var id = el.getAttribute('data-section-id');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function addToCart(variantId, quantity) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        id: parseInt(variantId, 10),
        quantity: quantity || 1,
        sections: getCartSectionIds(), // ask Shopify to render updated section HTML
      }),
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (e) { throw e; });
      return r.json();
    });
  }

  // Mirrors what product-form.js does: dispatch a 'cart:update' event (= CartAddEvent.eventName)
  // with event.detail.data.sections populated. cart-items-component calls morphSection() using
  // that HTML — single update, no polling, no re-render loop.

  // Module-scoped so refreshMinicart can pause the subtree observer across
  // FTF detach/dispatch/reattach — without pausing, those three mutations fed
  // the cascade back into processSSWidget() and re-triggered the cart section
  // diff on top of itself.
  /** @type {MutationObserver | null} */ var observer = null;
  /** @type {Element | null} */ var observerTarget = null;
  var observerConfig = { childList: true, subtree: true };

  function withObserverPaused(/** @type {() => void} */ fn) {
    if (observer) observer.disconnect();
    try {
      fn();
    } finally {
      if (observer && observerTarget) {
        observer.observe(observerTarget, observerConfig);
      }
    }
  }

  // Serialize cart updates so rapid clicks don't silently drop intermediate
  // state (which is what the old `_refreshing = true; setTimeout(..., 500)`
  // throttle did). Each queued run waits 300ms after its mutations settle so
  // morphSection is done before the next refreshMinicart starts.
  /** @type {Promise<void>} */ var refreshQueue = Promise.resolve();

  function refreshMinicart(/** @type {Record<string, any>} */ addResponse) {
    refreshQueue = refreshQueue.then(function () {
      return new Promise(function (resolve) {
        withObserverPaused(function () {
          const ftfPanel = document.getElementById('finish-the-fit-panel');
          const ftfParent = ftfPanel ? ftfPanel.parentNode : null;
          const ftfAnchor = ftfPanel ? ftfPanel.nextSibling : null;
          if (ftfPanel && ftfParent) ftfParent.removeChild(ftfPanel);

          var ev = new Event('cart:update', { bubbles: true });
          /** @type {any} */ (ev).detail = {
            data: { sections: (addResponse && addResponse.sections) || {} },
          };
          document.dispatchEvent(ev);

          if (ftfPanel && ftfParent) ftfParent.insertBefore(ftfPanel, ftfAnchor);
        });
        setTimeout(resolve, 300);
      });
    });
    return refreshQueue;
  }

  // ---------------------------------------------------------------------------
  // Close all open custom dropdowns except the given one
  // ---------------------------------------------------------------------------

  function closeAllDropdowns(/** @type {Element|null} */ except) {
    document.querySelectorAll('.ss-size-wrap.is-open').forEach(function (w) {
      if (w !== except) w.classList.remove('is-open');
    });
  }

  // Close dropdowns when clicking outside
  document.addEventListener('click', function (e) {
    if (!e.target || !(/** @type {Element} */ (e.target)).closest('.ss-size-wrap')) {
      closeAllDropdowns(null);
    }
  });

  // ---------------------------------------------------------------------------
  // Shared: custom size picker + "Add to Cart" button.
  //   swatchItems — array of .product-thumbnail__swatch-item elements (may be empty)
  //   btnClass    — CSS class for the ATC button
  //   fixedVariantId — used directly when swatchItems is empty (no size choice)
  // ---------------------------------------------------------------------------

  function buildSizeWidget(swatchItems, btnClass, /** @type {string} */ fixedVariantId) {
    var wrap = document.createElement('div');
    wrap.className = 'ss-size-wrap';

    /** @type {string} currently selected variant ID */
    var selectedVid = fixedVariantId || '';

    // ── Size trigger & dropdown (only when there are multiple options) ──────
    if (swatchItems.length > 1) {
      // Trigger label: "Size: — ▾"
      var trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'ss-size-trigger';
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');

      var triggerLabel = document.createElement('span');
      triggerLabel.className = 'ss-size-trigger__label';
      triggerLabel.textContent = 'Size: —';

      var triggerArrow = document.createElement('span');
      triggerArrow.className = 'ss-size-trigger__arrow';
      triggerArrow.setAttribute('aria-hidden', 'true');

      trigger.appendChild(triggerLabel);
      trigger.appendChild(triggerArrow);

      // Dropdown list
      var list = document.createElement('ul');
      list.className = 'ss-size-list';
      list.setAttribute('role', 'listbox');

      var placeholder = document.createElement('li');
      placeholder.className = 'ss-size-option ss-size-option--placeholder';
      placeholder.setAttribute('role', 'presentation');
      placeholder.setAttribute('aria-hidden', 'true');
      placeholder.textContent = 'Size: ...';
      placeholder.addEventListener('click', function () {
        trigger.setAttribute('aria-expanded', 'false');
        wrap.classList.remove('is-open');
      });
      list.appendChild(placeholder);

      // Sort: available sizes first, sold-out at the bottom
      var sortedItems = swatchItems.slice().sort(function (a, b) {
        var aOut = a.classList.contains('sold-out') ? 1 : 0;
        var bOut = b.classList.contains('sold-out') ? 1 : 0;
        return aOut - bOut;
      });

      sortedItems.forEach(function (item) {
        var vid = item.getAttribute('data-variant-id') || '';
        var label = item.textContent.trim();
        var isSoldOut = item.classList.contains('sold-out');

        var li = document.createElement('li');
        li.className = 'ss-size-option' + (isSoldOut ? ' is-sold-out' : '');
        li.setAttribute('role', 'option');
        li.setAttribute('data-vid', vid);
        li.textContent = label + (isSoldOut ? ' — Sold Out' : '');
        li.setAttribute('aria-disabled', isSoldOut ? 'true' : 'false');

        if (!isSoldOut) {
          li.addEventListener('click', function () {
            selectedVid = vid;
            triggerLabel.textContent = 'Size: ' + label;
            placeholder.textContent = 'Size: ' + label;
            trigger.setAttribute('aria-expanded', 'false');
            wrap.classList.remove('is-open');
            // Mark active
            list.querySelectorAll('.ss-size-option').forEach(function (o) {
              o.classList.toggle('is-active', o === li);
            });
          });
        }

        list.appendChild(li);
      });

      var firstAvail = list.querySelector('.ss-size-option:not(.is-sold-out):not(.ss-size-option--placeholder)');
      if (firstAvail) {
        selectedVid = firstAvail.getAttribute('data-vid') || '';
        var firstLabel = firstAvail.textContent.trim();
        triggerLabel.textContent = 'Size: ' + firstLabel;
        firstAvail.classList.add('is-active');
        placeholder.textContent = 'Size: ' + firstLabel;
      }

      trigger.addEventListener('click', function (e) {
        e.stopPropagation();
        var opening = !wrap.classList.contains('is-open');
        closeAllDropdowns(wrap);
        wrap.classList.toggle('is-open', opening);
        trigger.setAttribute('aria-expanded', opening ? 'true' : 'false');
      });

      wrap.appendChild(trigger);
      wrap.appendChild(list);

    } else if (swatchItems.length === 1) {
      // Single size — just show the label, no dropdown
      var singleItem = swatchItems[0];
      selectedVid = singleItem.getAttribute('data-variant-id') || fixedVariantId || '';
      var singleLabel = document.createElement('span');
      singleLabel.className = 'ss-size-single';
      singleLabel.textContent = 'Size: ' + singleItem.textContent.trim();
      wrap.appendChild(singleLabel);
    }
    // If no swatchItems and no fixedVariantId, no size UI is shown

    // ── Add to Cart button ────────────────────────────────────────────────────
    var btn = document.createElement('button');
    btn.className = btnClass || 'ss-atc-btn';
    btn.type = 'button';
    btn.textContent = 'Add to Cart';

    btn.addEventListener('click', function () {
      var vid = selectedVid;
      if (!vid) {
        // Highlight the trigger to prompt size selection
        wrap.classList.add('needs-size');
        setTimeout(function () { wrap.classList.remove('needs-size'); }, 1200);
        return;
      }
      btn.disabled = true;
      btn.textContent = 'Adding…';
      // Disable the cart rows while our add is in flight so qty/remove clicks
      // don't stack on top of this mutation (Horizon does the same via this
      // class on its own cart-items-component qty/remove path).
      var cartItemsComponents = document.querySelectorAll('cart-items-component');
      cartItemsComponents.forEach(function (el) { el.classList.add('cart-items-disabled'); });
      function clearCartItemsDisabled() {
        cartItemsComponents.forEach(function (el) { el.classList.remove('cart-items-disabled'); });
      }
      addToCart(vid)
        .then(function (response) {
          if (window.SearchspringBeacon && typeof window.SearchspringBeacon.trackAddToCartFromElement === 'function') {
            window.SearchspringBeacon.trackAddToCartFromElement(btn, { qty: 1 });
          }
          btn.textContent = 'Added!';
          refreshMinicart(response);
          setTimeout(function () {
            btn.textContent = 'Add to Cart';
            btn.disabled = false;
          }, 2000);
        })
        .catch(function () {
          btn.textContent = 'Error – try again';
          btn.disabled = false;
        })
        .finally(clearCartItemsDisabled);
    });

    wrap.appendChild(btn);
    return wrap;
  }

  // ---------------------------------------------------------------------------
  // Task 1: Replace inline swatch list with dropdown inside the SS widget
  // ---------------------------------------------------------------------------

  function transformSwatchesInContainer(container) {
    container
      .querySelectorAll('.product-thumbnail__swatch:not([data-ss-transformed])')
      .forEach(function (swatchWrapper) {
        var items = Array.from(
          swatchWrapper.querySelectorAll('.product-thumbnail__swatch-item')
        );

        swatchWrapper.setAttribute('data-ss-transformed', '1');

        // Hide the original items list but keep it in the DOM for SS tracking
        var origList = swatchWrapper.querySelector('.product-thumbnail__swatch-items');
        if (origList) origList.style.display = 'none';

        // For products with no size options, fall back to the default variant from the swym button
        var result = swatchWrapper.closest('.ss__result');
        var swymBtn = result && result.querySelector('.swym-button[data-variant-id]');
        var fixedVid = swymBtn ? (swymBtn.getAttribute('data-variant-id') || '') : '';

        swatchWrapper.appendChild(buildSizeWidget(items, 'ss-atc-btn', fixedVid));
      });
  }

  // ---------------------------------------------------------------------------
  // Task 2: Finish The Fit panel
  // ---------------------------------------------------------------------------

  function buildFTFCard(ssResult) {
    var imgEl = ssResult.querySelector('.image-element__wrap img');
    var titleEl = ssResult.querySelector('.product-thumbnail__title');
    var priceEl = ssResult.querySelector('.ss__price');
    var swatchItems = Array.from(ssResult.querySelectorAll('.product-thumbnail__swatch-item'));

    if (!titleEl) return null;

    var productUrl = titleEl.getAttribute('href') || '#';

    var card = document.createElement('div');
    card.className = 'ftf-card';

    // Image
    if (imgEl) {
      var imgA = document.createElement('a');
      imgA.href = productUrl;
      var img = document.createElement('img');
      img.src = imgEl.currentSrc || imgEl.src || imgEl.getAttribute('data-src') || '';
      img.alt = titleEl.textContent.trim();
      img.className = 'ftf-card__img';
      imgA.appendChild(img);
      card.appendChild(imgA);
    }

    // Info block
    var info = document.createElement('div');
    info.className = 'ftf-card__info';

    var nameA = document.createElement('a');
    nameA.href = productUrl;
    nameA.className = 'ftf-card__name';
    nameA.textContent = titleEl.textContent.trim();
    info.appendChild(nameA);

    // Fall back to the default variant from the swym button when no size options exist
    var swymBtn = ssResult.querySelector(
      '.swym-button[data-product-id], .swym-button[data-variant-id], .swym-button',
    );
    var fixedVid = swymBtn ? swymBtn.getAttribute('data-variant-id') || '' : '';
    var productIdAttr = ssResult.querySelector('[data-product-id]');
    var productId =
      (swymBtn && swymBtn.getAttribute('data-product-id')) ||
      ssResult.getAttribute('data-product-id') ||
      ssResult.getAttribute('data-id') ||
      (productIdAttr ? productIdAttr.getAttribute('data-product-id') : null);
    if (!fixedVid) {
      fixedVid =
        ssResult.getAttribute('data-variant-id') ||
        ssResult.getAttribute('data-uid') ||
        '';
    }
    if (productId) card.setAttribute('data-product-id', productId);
    if (fixedVid) card.setAttribute('data-variant-id', fixedVid);
    if (productUrl && productUrl !== '#') {
      card.setAttribute('data-product-href', productUrl);
    }

    if (swatchItems.length || fixedVid) {
      var sizeWidget = buildSizeWidget(swatchItems, 'ftf-atc-btn', fixedVid);

      if (priceEl) {
        var price = document.createElement('div');
        price.className = 'ftf-card__price';
        price.textContent = priceEl.textContent.trim();
        var atcBtn = sizeWidget.querySelector('.ss-atc-btn, .ftf-atc-btn');
        sizeWidget.insertBefore(price, atcBtn || null);
      }

      info.appendChild(sizeWidget);
    } else if (priceEl) {
      var fallbackPrice = document.createElement('div');
      fallbackPrice.className = 'ftf-card__price';
      fallbackPrice.textContent = priceEl.textContent.trim();
      info.appendChild(fallbackPrice);
    }

    card.appendChild(info);
    return card;
  }

  function syncSideCartResponseId(ssWidget) {
    if (!window.SearchspringBeacon || !window.SearchspringBeacon.setRecommendationResponse) {
      return;
    }

    var host =
      ssWidget.querySelector('[searchspring-recommend]') ||
      ssWidget.querySelector('[data-response-id]') ||
      ssWidget;
    var responseId =
      host.getAttribute('data-response-id') ||
      host.getAttribute('data-ss-response-id') ||
      host.getAttribute('response-id') ||
      '';

    if (!responseId) {
      var nodes = ssWidget.querySelectorAll('[data-response-id], [data-ss-response-id]');
      for (var i = 0; i < nodes.length; i++) {
        responseId =
          nodes[i].getAttribute('data-response-id') ||
          nodes[i].getAttribute('data-ss-response-id') ||
          '';
        if (responseId) break;
      }
    }

    if (responseId) {
      window.SearchspringBeacon.setRecommendationResponse('side-cart', responseId);
    }
  }

  function buildOrUpdateFTFPanel(ssWidget) {
    // Desktop only — do not create or update the panel on mobile
    if (window.innerWidth < 768) return;

    var results = Array.from(ssWidget.querySelectorAll('.ss__result'));
    if (!results.length) return;

    var isNew = !document.getElementById('finish-the-fit-panel');
    var panel;

    if (isNew) {
      panel = document.createElement('div');
      panel.id = 'finish-the-fit-panel';
      panel.className = 'finish-the-fit-panel';

      var headerEl = document.createElement('div');
      headerEl.className = 'ftf-header';
      headerEl.innerHTML = '<h3 class="ftf-title">FINISH THE FIT</h3>';
      panel.appendChild(headerEl);

      var listEl = document.createElement('div');
      listEl.id = 'ftf-product-list';
      listEl.className = 'ftf-product-list';
      panel.appendChild(listEl);

      // Mount inside .cart-drawer__ftf-anchor (data-skip-subtree-update) so the
      // panel is preserved across cart-items section morphs. Without the anchor,
      // morph removes the FTF panel because it's appended client-side and isn't
      // in the server HTML — the visible remove/re-add gap was the user-reported
      // recs flicker on PLP add-to-cart.
      var ftfAnchor = document.querySelector('.cart-drawer__ftf-anchor');
      var cartDialog = document.querySelector('.cart-drawer__dialog');
      if (ftfAnchor) {
        ftfAnchor.appendChild(panel);
      } else if (cartDialog) {
        cartDialog.appendChild(panel);
      } else {
        document.body.appendChild(panel);
      }

      watchCartDialogState(panel);
    } else {
      panel = document.getElementById('finish-the-fit-panel');
      // Migrate from old position (direct child of dialog) into the anchor on
      // first run after deploy.
      var ftfAnchorMigrate = document.querySelector('.cart-drawer__ftf-anchor');
      if (ftfAnchorMigrate && panel.parentElement !== ftfAnchorMigrate) {
        ftfAnchorMigrate.appendChild(panel);
      }
    }

    const list = document.getElementById('ftf-product-list');
    if (!list) return;

    // Build new cards from current SS results, keyed by product URL so we can
    // reuse existing DOM nodes and avoid wiping + recreating on every SS re-render.
    /** @type {Element[]} */
    const newCards = [];
    results.forEach(function (result) {
      const card = buildFTFCard(result);
      if (card) newCards.push(card);
    });

    /** @param {Element} card @returns {string} */
    function cardKey(card) {
      const a = card.querySelector('a.ftf-card__name');
      return a ? a.getAttribute('href') || '' : '';
    }

    const existingCards = Array.from(list.children);
    const newKeys = newCards.map(cardKey);
    const existingKeys = existingCards.map(cardKey);

    // Nothing changed — skip DOM work entirely
    if (newKeys.join('|') === existingKeys.join('|')) {
      syncSideCartResponseId(ssWidget);
      return;
    }

    // Map existing nodes by key so unchanged products reuse their DOM elements
    /** @type {Record<string, Element>} */
    const byKey = {};
    existingCards.forEach(function (card) {
      const k = cardKey(card);
      if (k) byKey[k] = card;
    });

    const fragment = document.createDocumentFragment();
    newCards.forEach(function (newCard) {
      const k = cardKey(newCard);
      if (k && byKey[k]) {
        fragment.appendChild(byKey[k]); // reuse — no flicker for this card
        delete byKey[k];
      } else {
        fragment.appendChild(newCard);
      }
    });

    // Remove cards that are no longer in the results
    Object.keys(byKey).forEach(function (k) {
      const stale = byKey[k];
      if (stale) list.removeChild(stale);
    });

    list.appendChild(fragment);
    syncSideCartResponseId(ssWidget);
  }

  // Show / hide the FTF panel in sync with the cart drawer dialog
  function watchCartDialogState(ftfPanel) {
    var cartDialog = document.querySelector('.cart-drawer__dialog');
    if (!cartDialog) return;

    function syncPanel() {
      if (cartDialog.hasAttribute('open')) {
        ftfPanel.classList.add('is-open');
      } else {
        ftfPanel.classList.remove('is-open');
      }
    }

    var obs = new MutationObserver(syncPanel);
    obs.observe(cartDialog, { attributes: true, attributeFilter: ['open'] });

    // Sync immediately in case the drawer is already open
    syncPanel();

    // Guard: only needed when panel is outside the dialog and backdrop may treat clicks as outside.
    var drawerComponent = cartDialog.closest('cart-drawer-component');
    if (drawerComponent && !cartDialog.contains(ftfPanel)) {
      drawerComponent.addEventListener('click', function (e) {
        var me = /** @type {MouseEvent} */ (e);
        var rect = ftfPanel.getBoundingClientRect();
        if (
          me.clientX >= rect.left &&
          me.clientX <= rect.right &&
          me.clientY >= rect.top &&
          me.clientY <= rect.bottom
        ) {
          e.stopImmediatePropagation();
        }
      }, true); // capture phase — fires before #handleClick
    }
  }

  // ---------------------------------------------------------------------------
  // Side-cart SnapUI bootstrap (fixes script-in-DOM but no widget race)
  // ---------------------------------------------------------------------------

  const SIDE_CART_WIDGET_ID = 'ss__recommendation--side-cart';
  const SIDE_CART_ANCHOR_SELECTOR = '.cart-drawer__rec-anchor';
  const SIDE_CART_SCRIPT_SELECTOR =
    'script[type="searchspring/personalized-recommendations"][profile="side-cart"]';
  let sideCartKickInFlight = false;
  let sideCartKickCooldownUntil = 0;

  /** @type {Element | null} */
  let savedSideCartWidget = null;

  function parkSideCartWidget() {
    const widget = document.getElementById(SIDE_CART_WIDGET_ID);
    const anchor = document.querySelector(SIDE_CART_ANCHOR_SELECTOR);
    if (!widget || !anchor) return;
   
    const dialog = document.querySelector('.cart-drawer__dialog');
    const searchRoot = dialog || document;
    searchRoot
      .querySelectorAll(`[id="${SIDE_CART_WIDGET_ID}"]`)
      .forEach((el) => {
        if (el !== widget && el.parentNode) el.parentNode.removeChild(el);
      });
    if (widget.parentElement !== anchor) {
      anchor.appendChild(widget);
    }
  }

  function restoreSideCartWidget() {
    if (document.getElementById(SIDE_CART_WIDGET_ID) || !savedSideCartWidget) return;
    const anchor = document.querySelector(SIDE_CART_ANCHOR_SELECTOR);
    if (anchor) {
      while (anchor.firstChild) anchor.removeChild(anchor.firstChild);
      anchor.appendChild(savedSideCartWidget);
      return;
    }
    const summary = document.querySelector('.cart-drawer__summary');
    if (summary) summary.insertAdjacentElement('beforebegin', savedSideCartWidget);
  }

  function dedupeInnerHosts(/** @type {Element} */ widget) {
    const hosts = Array.from(
      widget.querySelectorAll('[searchspring-recommend]'),
    );
    if (hosts.length <= 1) return;
    hosts.slice(1).forEach((el) => {
      if (el.parentNode) el.parentNode.removeChild(el);
    });
  }

  function setupSideCartWidgetObserver() {
    const parkObserver = new MutationObserver(() => {
      const current = document.getElementById(SIDE_CART_WIDGET_ID);
      if (current) {
        savedSideCartWidget = current;
        dedupeInnerHosts(current);
        parkSideCartWidget();
      } else if (savedSideCartWidget) {
        restoreSideCartWidget();
      }
    });
    // Prefer the cart dialog; fall back to body for the empty-cart state where
    // the anchor and config script don't exist yet.
    const dialog = document.querySelector('.cart-drawer__dialog');
    parkObserver.observe(dialog || document.body, {
      childList: true,
      subtree: true,
    });
  }

  function getSideCartConfigScript() {
    return document.querySelector(SIDE_CART_SCRIPT_SELECTOR);
  }

  function isCartDrawerEmpty() {
    return Boolean(document.getElementById('cart-drawer-heading-empty'));
  }

  /**
   * SnapUI scans recommendation scripts when bundle.js runs. If the side-cart
   * script is injected later (cart morph after ATC), it is never registered —
   * no widget, no recommendations API call. Throttled networks make this common.
   * @param {() => void} callback
   */
  function whenSnapUIBundleReady(callback) {
    if (window.SearchSpring || window.searchspring) {
      callback();
      return;
    }

    var bundleTag = document.getElementById('searchspring-context');
    if (!bundleTag) return;

    if (bundleTag.dataset.ssBundleReady === '1') {
      callback();
      return;
    }

    function markReady() {
      bundleTag.dataset.ssBundleReady = '1';
      callback();
    }

    bundleTag.addEventListener('load', markReady, { once: true });

    // Inline script + deferred src may already be complete when we attach.
    setTimeout(function () {
      if (window.SearchSpring || window.searchspring) {
        markReady();
      }
    }, 0);
  }

  /**
   * Re-insert the side-cart config script so SnapUI's scanner picks it up.
   *
   */
  function reinsertSideCartConfigScript() {
    var script = getSideCartConfigScript();
    if (!script || !script.parentNode) return;
    if (script.hasAttribute('data-ss-kicked')) return;

    var replacement = document.createElement('script');
    replacement.type = 'searchspring/personalized-recommendations';
    replacement.setAttribute('profile', 'side-cart');
    replacement.setAttribute('data-ss-kicked', '1');
    replacement.textContent = script.textContent;
    script.parentNode.replaceChild(replacement, script);
  }

  /**
   * Config script present but widget missing — kick SnapUI with bounded retries.
   */
  function tryKickSideCartRecommendations() {
    if (sideCartKickInFlight || Date.now() < sideCartKickCooldownUntil) return;
    if (isCartDrawerEmpty()) return;
    if (!getSideCartConfigScript()) return;
    if (document.getElementById(SIDE_CART_WIDGET_ID)) return;
    if (document.querySelector('[searchspring-recommend="side-cart"]')) return;

    sideCartKickInFlight = true;

    whenSnapUIBundleReady(function () {
      var attempt = 0;
      var maxAttempts = 10;

      function step() {
        if (!getSideCartConfigScript() || isCartDrawerEmpty()) {
          sideCartKickInFlight = false;
          return;
        }

        if (document.getElementById(SIDE_CART_WIDGET_ID)) {
          sideCartKickInFlight = false;
          sideCartKickCooldownUntil = Date.now() + 1500;
          processSSWidget();
          return;
        }

        if (attempt >= maxAttempts) {
          sideCartKickInFlight = false;
          sideCartKickCooldownUntil = Date.now() + 3000;
          return;
        }

        if (attempt === 0) {
          reinsertSideCartConfigScript();
        }

        attempt += 1;
        setTimeout(step, 450);
      }

      step();
    });
  }

  // ---------------------------------------------------------------------------
  // Main: watch for the SS widget to finish rendering, then process it
  // ---------------------------------------------------------------------------

  var lastSwatchCount = 0;

  function processSSWidget() {
    var widget = document.getElementById(SIDE_CART_WIDGET_ID);
    if (!widget) {
      // Widget absence has two causes, and only one warrants cleanup:
      //   1. Empty-cart transition — cart-items-component swapped to the empty
      //      template, SS config script gone, widget won't come back. Clean up FTF.
      //   2. Transient mid-morph absence (FTF-add refreshMinicart, ATC-error
      //      CartAddEvent with empty sections, etc.) — widget wiped briefly while
      //      morphSection + SS re-render. Widget will come back.
      //   3. SnapUI never registered side-cart (script added after bundle scan).
      // Positive signal for (1): #cart-drawer-heading-empty is rendered into the
      // live DOM only when the empty-cart template is actually applied. Row-count
      // or widget-presence checks are both ambiguous during transient morphs.
      if (isCartDrawerEmpty()) {
        const stalePanel = document.getElementById('finish-the-fit-panel');
        if (stalePanel && stalePanel.isConnected) {
          stalePanel.remove();
        }
        lastSwatchCount = 0;
      } else if (getSideCartConfigScript()) {
        tryKickSideCartRecommendations();
      }
      return;
    }

    var results = widget.querySelectorAll('.ss__result');
    if (!results.length) return;

    var swatches = widget.querySelectorAll('.product-thumbnail__swatch-item');
    var currentSwatchCount = swatches.length;
    var panel = document.getElementById('finish-the-fit-panel');
    var panelMissingOrDetached = !panel || !panel.isConnected;

    // Re-process when content changed OR panel was removed during drawer/cart re-render.
    if (!panelMissingOrDetached && currentSwatchCount === lastSwatchCount) return;
    lastSwatchCount = currentSwatchCount;

    dedupeInnerHosts(widget);

    if (window.innerWidth >= 768) {
      transformSwatchesInContainer(widget);
      buildOrUpdateFTFPanel(widget);
    } else {
      transformSlidesForMobile(widget);
      patchSwiperForMobile(widget);
    }
  }

  function transformSlidesForMobile(widget) {
    // Hide the SS quick-add panel (popup mechanism)
    var quickAdd = widget.querySelector('.ss__results--quick-add');
    if (quickAdd) quickAdd.style.display = 'none';

    widget.querySelectorAll('.swiper-slide:not([data-ss-mobile-transformed])').forEach(function (slide) {
      slide.setAttribute('data-ss-mobile-transformed', '1');

      // Hide the SS "Add To Bag ++" button that triggers the popup
      var ssAtcBtn = slide.querySelector('.ss__atc--button');
      if (ssAtcBtn) ssAtcBtn.style.display = 'none';

      // Match swatch items from quick-add section by product-id
      var swymBtn = slide.querySelector('.swym-button[data-product-id]');
      var productId = swymBtn ? swymBtn.getAttribute('data-product-id') : null;
      var swatchItemsContainer = productId
        ? widget.querySelector('.product-thumbnail__swatch-items[data-product-id="' + productId + '"]')
        : null;
      var swatchItems = swatchItemsContainer
        ? Array.from(swatchItemsContainer.querySelectorAll('.product-thumbnail__swatch-item'))
        : [];
      var fixedVid = swymBtn ? (swymBtn.getAttribute('data-variant-id') || '') : '';

      var caption = slide.querySelector('.thumbnail__caption');
      if (caption && (swatchItems.length || fixedVid)) {
        caption.appendChild(buildSizeWidget(swatchItems, 'ss-atc-btn', fixedVid));
      }
    });
  }

  function patchSwiperForMobile(widget) {
    var swiperEl = widget.querySelector('.swiper');
    if (!swiperEl || !swiperEl.swiper) return;
    var swiper = swiperEl.swiper;
    swiper.params.slidesPerView = 1.5;
    swiper.params.breakpoints = {}; // clear any breakpoints that override slidesPerView
    swiper.update();
  }

  function init() {
    setupSideCartWidgetObserver();
    parkSideCartWidget(); // park immediately if widget already exists at load time
    processSSWidget();

    /** @type {ReturnType<typeof setTimeout> | null} */
    let debounceTimer = null;

    // Capture the instance in a local so TS narrows it as non-null inside
    // attachObserver(); also publish to the module-scoped `observer` so
    // refreshMinicart's withObserverPaused() can disconnect/reconnect around
    // the FTF dance. Debounce bumped 80 → 250ms: morphSection's partial
    // updates used to trigger the callback multiple times per logical cart
    // change; 250ms coalesces them into one.
    const mo = new MutationObserver(function () {
      if (debounceTimer !== null) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(processSSWidget, 250);
    });
    observer = mo;

    // Prefer observing just the cart drawer; fall back to body until it exists
    function attachObserver() {
      const cartDrawer = document.querySelector(
        '.cart-drawer__dialog, cart-drawer-component',
      );
      observerTarget = cartDrawer || document.body;
      mo.observe(observerTarget, observerConfig);
    }

    attachObserver();

    document.addEventListener('cart:update', function () {
      setTimeout(tryKickSideCartRecommendations, 400);
    });

    window.addEventListener('ss-on-results-load', function () {
      setTimeout(processSSWidget, 0);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
